import { Component, ElementRef, EventEmitter, Input, Output, QueryList, ViewChildren } from '@angular/core';
import { forkJoin } from 'rxjs';
import { canceledColor, cleanDescription, formatDate, getStatusColor, getStatusName, shippedColor } from '../../master-schedule/shared';
import { ApiService } from 'src/app/shared/services/api.service';
import { CommentView, MasterScheduleActivityCommentView } from 'src/app/shared/data/data.generated';
import { GetProductCodeDescription } from 'src/app/shared/data/product-codes';

@Component({
  selector: 'project-card',
  templateUrl: './project-card.component.html',
  styleUrl: './project-card.component.scss',
  standalone: false,
})
export class ProjectCardComponent {

  constructor(private readonly api: ApiService) { }

  @Input() public projectData: any = null;
  @Input() public canSelectProject: boolean = false;
  @Input() public selectedProject: any = null;
  @Output() public selectedProjectChange = new EventEmitter<any>();
  @Input() public toggleMultiSelect: (type: string, event: MouseEvent) => void = () => { };
  @Input() public hideMultiSelect: () => void = () => { };
  @Input() public showViewIcons: boolean = false;
  @Output() public selectedViewChange = new EventEmitter<ProjectCardView>();
  @Output() public hotToggleLoadingChange = new EventEmitter<boolean>();
  @Output() public projectNamesDuplicated = new EventEmitter<number[]>();

  @ViewChildren('customNameRoleInput') public customNameRoleInputs!: QueryList<ElementRef<HTMLInputElement>>;

  // Closure fixes to pass the correct context to multi-select dropdowns
  public getOptions = (type: string, search: string, maxResults?: number) => this.api.MasterSchedule.GetMasterScheduleFilterList(type, search, maxResults);

  public notesModalVisible: boolean = false;
  public notes: MasterScheduleActivityCommentView[] = [];
  public newProjectNote: string = '';
  public isAddingProjectNote: boolean = false;
  
  public getStatusColor = getStatusColor;
  public shippedColor = shippedColor;
  public formatDate = formatDate;
  public getProductCodeDescription = GetProductCodeDescription;
  public icons: { key: string, value: string }[] = [];
  public ProjectCardView = ProjectCardView;

  public editNamesModalVisible: boolean = false;
  public standardNames: RoleAndName[] = [];
  public customNames: RoleAndName[] = [];

  public duplicateNamesModalVisible: boolean = false;
  public duplicateNamesSelectedProjectIds: number[] = [];
  public isDuplicatingNames: boolean = false;

  public templateModalVisible: boolean = false;
  public templateOptions: string[] = [];
  public selectedTemplateName: string = '';
  private readonly wipeAndResetSuffix: string = ' (Wipe and Reset)';
  public isSavingTemplate: boolean = false;

  public showDetails: boolean = false;
  @Input() public viewVisible: ProjectCardView | null = null;
  public isTogglingHot: boolean = false;

  ngOnInit() {
    this.icons = [];
    if (this.projectData?.LatestActivityIcon) {
      this.projectData.LatestActivityIcon.split('|').forEach((pair: string) => {
        const [key, value] = pair.split(':').map(s => s.trim());
        if (key && value) {
          this.icons.push({ key, value });
        }
      });
    }
    this.loadNamesFromData();
  }

  getHeight(): string {
    var baseHeight = 135;
    if (this.projectData?.CustomNames || this.projectData?.MechanicalEngineer || this.projectData?.MechanicalDrafter || this.projectData?.ElectricalEngineer || this.projectData?.ElectricalDrafter)
      baseHeight += 20;
    return `${baseHeight}px`;
  }

  getStatusTooltip(): string {
    if (this.projectData?.IsOnHold) {
      return 'On Hold';
    }
    if (this.projectData?.HasShipped) {
      return 'Shipped';
    }
    return `${getStatusName(this.projectData?.OverallStatusId)}`;
  }

  getProjectStatusColor(): string {
    if (this.projectData?.TemplateName === 'Canceled') {
      return canceledColor;
    }
    return this.projectData?.IsOnHold ? getStatusColor(5) : this.projectData?.HasShipped ? shippedColor : getStatusColor(this.projectData?.OverallStatusId);
  }

  formatSalesRepName(value: string | null | undefined): string {
    if (!value) {
      return '';
    }

    return value.replace(/^.+?\s-\s\d{4}\s+/, '');
  }

  toBrowserDate(timestamp: string | Date | null | undefined): Date | null {
    if (!timestamp) {
      return null;
    }

    if (timestamp instanceof Date) {
      return timestamp;
    }

    const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(timestamp);
    const date = new Date(hasTimezone ? timestamp : `${timestamp}Z`);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  formatPercentComplete(value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === '' || !value) {
      return 'N/A';
    }

    const percentComplete = Number(value);
    return Number.isNaN(percentComplete) ? 'N/A' : `${percentComplete.toFixed(1)}%`;
  }

  formatCompletedEstimatedHours(completedHoursValue: number | string | null | undefined, estimatedHoursValue: number | string | null | undefined): string {
    const completedHours = Number(completedHoursValue);
    const estimatedHours = Number(estimatedHoursValue);

    if (Number.isNaN(completedHours) || Number.isNaN(estimatedHours) || completedHours <= 0 || estimatedHours <= 0) {
      return '';
    }

    return `(${completedHours.toFixed(1)} / ${estimatedHours.toFixed(1)})`;
  }

  loadNamesFromData()
  {
    this.customNames = [];
    if (this.projectData?.CustomNames) {
      this.projectData.CustomNames.split('|').forEach((pair: string) => {
        const [key, value] = pair.split(';').map(s => s.trim());
        if (key && value) {
          this.customNames.push(new RoleAndName('', key, value));
        }
      });
    }
    this.standardNames = [];
    this.standardNames.push(new RoleAndName('SalesRep', 'Sales Rep', this.projectData?.SalesRep ?? ''));
    this.standardNames.push(new RoleAndName('SalesManager', 'Sales Manager', this.projectData?.SalesManager ?? ''));
    this.standardNames.push(new RoleAndName('TechnicalEngineer', 'Project Manager', this.projectData?.TechnicalEngineer ?? ''));
    this.standardNames.push(new RoleAndName('ProjectManager', 'Project Engineer', this.projectData?.ProjectManager ?? ''));
    this.standardNames.push(new RoleAndName('ProjectManager2', 'Project Engineer (Alt)', this.projectData?.ProjectManager2 ?? ''));
    this.standardNames.push(new RoleAndName('Planner', 'Planner', this.projectData?.Planner ?? ''));

    this.standardNames.push(new RoleAndName('MechanicalEngineer', 'Mechanical Engineer', this.projectData?.MechanicalEngineer ?? ''));
    this.standardNames.push(new RoleAndName('MechanicalDrafter', 'Mechanical Drafter', this.projectData?.MechanicalDrafter ?? ''));
    this.standardNames.push(new RoleAndName('ElectricalEngineer', 'Electrical Engineer', this.projectData?.ElectricalEngineer ?? ''));
    this.standardNames.push(new RoleAndName('ElectricalDrafter', 'Electrical Drafter', this.projectData?.ElectricalDrafter ?? ''));
  }

  onCardClick() {
    if (!this.canSelectProject) return;
    this.selectedProject = this.selectedProject === this.projectData ? null : this.projectData;
    this.selectedProjectChange.emit(this.selectedProject);
    if (!this.selectedProject) {
      this.hideDetails();
    }
  }

  onNotesClick(event: MouseEvent) {
    event.stopPropagation();
    this.newProjectNote = '';
    this.api.MasterSchedule.GetMasterScheduleActivityNotes(0, this.projectData.Id).subscribe(notes => {
      this.notes = notes;
      this.notesModalVisible = true;
    });
  }

  addProjectNote() {
    const note = this.newProjectNote.trim();
    if (!note || this.isAddingProjectNote || !this.projectData?.Id) {
      return;
    }

    this.isAddingProjectNote = true;
    this.api.MasterSchedule.InsertMasterScheduleProjectNote(this.projectData.Id, note)
      .subscribe({
        next: () => {
          this.newProjectNote = '';
          this.projectData.NoteCount = (this.projectData.NoteCount ?? 0) + 1;
          this.api.MasterSchedule.GetMasterScheduleActivityNotes(0, this.projectData.Id).subscribe(notes => {
            this.notes = notes;
          });
        },
        complete: () => {
          this.isAddingProjectNote = false;
        },
        error: () => {
          this.isAddingProjectNote = false;
        }
      });
  }

  toggleProjectHot(event: MouseEvent) {
    event.stopPropagation();
    if (this.isTogglingHot || !this.projectData?.Id) {
      return;
    }

    const previousIsHot = !!this.projectData.IsHot;
    const nextIsHot = !previousIsHot;
    this.projectData.IsHot = nextIsHot;
    this.isTogglingHot = true;
    this.hotToggleLoadingChange.emit(true);

    this.api.MasterSchedule.ToggleMasterScheduleProjectIsHot(this.projectData.Id, nextIsHot)
      .subscribe({
        next: () => {
          const noteText = nextIsHot ? 'Toggled as Hot' : 'Untoggled as Hot';
          this.api.MasterSchedule.InsertMasterScheduleProjectNote(this.projectData.Id, noteText)
            .subscribe(() => {
              this.projectData.NoteCount = (this.projectData.NoteCount ?? 0) + 1;
            });
        },
        error: () => {
          this.projectData.IsHot = previousIsHot;
          this.isTogglingHot = false;
          this.hotToggleLoadingChange.emit(false);
        },
        complete: () => {
          this.isTogglingHot = false;
          this.hotToggleLoadingChange.emit(false);
        }
      });
  }

  closeNotesModal() {
    this.notesModalVisible = false;
    this.newProjectNote = '';
    this.isAddingProjectNote = false;
  }

  editNames(event?: MouseEvent) {
    event?.stopPropagation();
    this.editNamesModalVisible = true;
  }

  deleteCustomName(key: string) {
    this.customNames = this.customNames.filter(name => name.Role !== key);
  }

  addCustomName() {
    this.customNames.push(new RoleAndName('', '', ''));
    // Runs after the new row renders so the table scrolls down to it.
    setTimeout(() => {
      const input = this.customNameRoleInputs?.last?.nativeElement;
      input?.scrollIntoView({ block: 'nearest' });
      input?.focus();
    });
  }

  closeEditNamesModal() {
    this.loadNamesFromData();
    this.editNamesModalVisible = false;
    this.hideMultiSelect();
  }

  openTemplateModal(event?: MouseEvent) {
    event?.stopPropagation();
    if (this.viewVisible === ProjectCardView.Schedule) {
      this.hideDetails();
    }

    const isRebuild = !!this.projectData?.ProductTemplateId;
    this.selectedTemplateName = '';
    this.templateOptions = [];
    this.api.MasterSchedule.GetMasterScheduleFilterList('Product', '').subscribe(options => {
      const currentTemplateName = this.projectData?.TemplateName;
      this.templateOptions = isRebuild && currentTemplateName
        ? options.filter(option => option !== currentTemplateName).concat(`${currentTemplateName}${this.wipeAndResetSuffix}`)
        : options;
    });
    this.templateModalVisible = true;
  }

  closeTemplateModal() {
    this.templateModalVisible = false;
  }

  isScheduleViewDisabled(): boolean {
    return !this.projectData?.ProductTemplateId || this.projectData?.TemplateName === 'Canceled';
  }

  saveTemplate() {
    if (this.isSavingTemplate || !this.projectData?.Id || !this.selectedTemplateName) {
      return;
    }

    this.performSaveTemplate();
  }

  private performSaveTemplate() {
    this.isSavingTemplate = true;
    this.templateModalVisible = false;
    const isRebuild = !!this.projectData?.ProductTemplateId;
    const previousTemplateName = this.projectData?.TemplateName || '(No Template)';
    const templateName = this.selectedTemplateName.endsWith(this.wipeAndResetSuffix)
      ? this.selectedTemplateName.slice(0, -this.wipeAndResetSuffix.length)
      : this.selectedTemplateName;
    const request = this.projectData?.ProductTemplateId
      ? this.api.MasterSchedule.RebuildMasterScheduleTemplate(this.projectData.Id, templateName)
      : this.api.MasterSchedule.BuildMasterScheduleTemplate(this.projectData.Id, templateName);

    request.subscribe({
      complete: () => {
        const operation = isRebuild ? 'Rebuilt' : 'Built';
        const note = `${operation} Template. Previous Template: ${previousTemplateName}. New Template: ${templateName}`;
        this.api.MasterSchedule.InsertMasterScheduleProjectNote(this.projectData.Id, note).subscribe({
          complete: () => {
            this.refreshProjectData();
          },
          error: error => {
            this.isSavingTemplate = false;
            alert(`Template saved, but the project comment could not be added: ${this.getApiErrorMessage(error)}`);
          }
        });
      },
      error: error => {
        this.isSavingTemplate = false;
        alert(`Unable to save template: ${this.getApiErrorMessage(error)}`);
      }
    });
  }

  private refreshProjectData() {
    this.reloadProjectData(() => {
      this.isSavingTemplate = false;
    });
  }

  reloadProjectData(onComplete: (() => void) | null = null) {
    if (!this.projectData?.ProjectNumber) {
      onComplete?.();
      return;
    }

    this.api.MasterSchedule.GetMasterScheduleProject(this.projectData.ProjectNumber).subscribe({
      next: project => {
        project.ItemDescription = cleanDescription(project.ItemDescription);
        project.PoDate = this.formatDate(project.PoDate);
        project.RequestedShipDate = this.formatDate(project.RequestedShipDate);
        project.AcknowledgedShipDate = this.formatDate(project.AcknowledgedShipDate);
        project.CurrentShipDate = this.formatDate(project.CurrentShipDate);
        project.ApprovalDrawingDate = this.formatDate(project.ApprovalDrawingDate);
        project.ActualShipDate = this.formatDate(project.ActualShipDate);
        Object.assign(this.projectData, project);
        this.loadNamesFromData();
      },
      complete: () => {
        onComplete?.();
      },
      error: error => {
        onComplete?.();
        alert(`The project could not be refreshed: ${this.getApiErrorMessage(error)}`);
      }
    });
  }

  private getApiErrorMessage(error: any): string {
    return error?.error?.message ?? error?.error ?? error?.message ?? 'An unexpected error occurred.';
  }

  saveEditedNames() {
    this.api.MasterSchedule.UpdateMasterScheduleProjectNames(this.projectData.Id, this.standardNames, this.customNames).subscribe(() => {
      this.applySavedNamesToProjectData();
      this.editNamesModalVisible = false;
      this.hideMultiSelect();
    });
  }

  openDuplicateNamesModal() {
    this.duplicateNamesSelectedProjectIds = [];
    this.duplicateNamesModalVisible = true;
  }

  closeDuplicateNamesModal() {
    this.duplicateNamesModalVisible = false;
    this.duplicateNamesSelectedProjectIds = [];
  }

  saveAndDuplicateNames() {
    if (this.isDuplicatingNames || this.duplicateNamesSelectedProjectIds.length === 0 || !this.projectData?.Id) {
      return;
    }

    this.isDuplicatingNames = true;
    const duplicatedProjectIds = [this.projectData.Id, ...this.duplicateNamesSelectedProjectIds];
    this.api.MasterSchedule.UpdateMasterScheduleProjectNames(this.projectData.Id, this.standardNames, this.customNames)
      .subscribe({
        next: () => {
          this.applySavedNamesToProjectData();
          forkJoin(this.duplicateNamesSelectedProjectIds
            .map(projectId => this.api.MasterSchedule.DuplicateMasterScheduleProjectNames(this.projectData.Id, projectId)))
            .subscribe({
              complete: () => {
                this.isDuplicatingNames = false;
                this.closeDuplicateNamesModal();
                this.editNamesModalVisible = false;
                this.hideMultiSelect();
                this.projectNamesDuplicated.emit(duplicatedProjectIds);
              },
              error: error => {
                this.isDuplicatingNames = false;
                alert(`Names saved, but they could not be duplicated: ${this.getApiErrorMessage(error)}`);
              }
            });
        },
        error: error => {
          this.isDuplicatingNames = false;
          alert(`Unable to save names: ${this.getApiErrorMessage(error)}`);
        }
      });
  }

  private applySavedNamesToProjectData() {
    this.projectData.CustomNames = this.customNames.map(name => `${name.Role};${name.Name}`).join('|');
    this.projectData.SalesManager = this.standardNames.find(name => name.Property === 'SalesManager')?.Name ?? '';
    this.projectData.ProjectManager = this.standardNames.find(name => name.Property === 'ProjectManager')?.Name ?? '';
    this.projectData.ProjectManager2 = this.standardNames.find(name => name.Property === 'ProjectManager2')?.Name ?? '';
    this.projectData.Planner = this.standardNames.find(name => name.Property === 'Planner')?.Name ?? '';
    this.projectData.TechnicalEngineer = this.standardNames.find(name => name.Property === 'TechnicalEngineer')?.Name ?? '';
    this.projectData.MechanicalEngineer = this.standardNames.find(name => name.Property === 'MechanicalEngineer')?.Name ?? '';
    this.projectData.MechanicalDrafter = this.standardNames.find(name => name.Property === 'MechanicalDrafter')?.Name ?? '';
    this.projectData.ElectricalEngineer = this.standardNames.find(name => name.Property === 'ElectricalEngineer')?.Name ?? '';
    this.projectData.ElectricalDrafter = this.standardNames.find(name => name.Property === 'ElectricalDrafter')?.Name ?? '';
  }

  toggleDetailsView(view: ProjectCardView, event: MouseEvent) {
    event.stopPropagation();
    if (view === ProjectCardView.Schedule && this.isScheduleViewDisabled()) {
      return;
    }

    if (this.viewVisible === view) {
      this.hideDetails();
    } else {
      this.selectedProjectChange.emit(this.projectData);
      this.showDetails = true;
      this.viewVisible = view;
      this.selectedViewChange.emit(view);
    }
  }

  hideDetails() {
    this.showDetails = false;
    this.viewVisible = ProjectCardView.None;
    this.selectedViewChange.emit(ProjectCardView.None);
  }
  
}

export class RoleAndName {
  constructor(
    public Property: string,
    public Role: string,
    public Name: string) { }
}

export enum ProjectCardView {
  None,
  OrderDetails,
  EngineeringCalculations,
  EngineeringDetails,
  Schedule,
  Documents
}